import { getCustomerAppBaseUrl } from "@theme/gadget-envs";

const QUICK_ORDER_TABLE_SELECTOR = '.quick-order-table';
const QUICK_ORDER_UPLOAD_POPULATING_SELECTOR = '[data-rs-upload-populating]';
const QUICK_ORDER_SKU_VALIDATING_CLASS = 'rs-quick-order-sku-validating';

export class inputValidation {
  constructor() {
    // API globals
    this.RTP = window.RTP ||= {cache: new Map(), inflight: new Map(), ctxCache: null};
    this.context = this.getContextFromCookie();
    this.salesArea = this.resolveSalesArea(this.context);
    this.salesOrg = this.salesArea?.sales_org_id;
    this.distributionChannel = this.salesArea?.distribution_channel_id;
    this.division = this.salesArea?.division_id;
    this.companyId = this.getContextFromCookie()?.company_id;
    this.externalCompanyId = this.context?.company_external_id;
    this.customerId = document.body.dataset.customerId;
    this.locationId = this.getContextFromCookie()?.location_id.replace('gid://shopify/CompanyLocation/', '');
    this.stateCode = this.getContextFromCookie()?.state_code;
    this.token = window.myJWT;
  }

  isQuickOrderInput(target) {
    return target instanceof HTMLInputElement && !!target.closest(QUICK_ORDER_TABLE_SELECTOR);
  }

  /** True while quick-order CSV/XLSX upload is writing rows (skip check-eligibility / check-visibility). */
  isQuickOrderUploadPopulating(target) {
    return !!(target instanceof Element && target.closest(QUICK_ORDER_UPLOAD_POPULATING_SELECTOR));
  }

  /**
   * Run eligibility checks for quick-order rows (used after a file upload fills the table).
   * Every row SKU goes through a batched `check-eligibility` POST, then SKUs share one visibility POST.
   * @param {Element[]} rows
   * @returns {Promise<boolean>}
   */
  async validateQuickOrderRows(rows = []) {
    /** @type {Array<{ row: Element, material: HTMLInputElement, quantity: HTMLInputElement | null, sku: string }>} */
    const entries = [];

    for (const row of rows) {
      const material = row.querySelector('.rs-material-number-input-wrapper input');
      const quantity = row.querySelector('.rs-quantity-input-wrapper input[type="number"]');
      const customerPartNumberSku = material?.nextElementSibling.querySelector(`.customer-part-number-suggestion[data-part-number="${material?.value.trim()}"]`);
      let sku = null;
      if (customerPartNumberSku && customerPartNumberSku.dataset.partNumber === material?.value.trim()) {
        sku = String(customerPartNumberSku.dataset.sku ?? '').trim();
      } else if (material?.getAttribute('data-selected-sku')) {
        sku = String(material?.getAttribute('data-selected-sku') ?? '').trim();
      } else {
        sku = String(material?.value ?? '').trim();
      }
      const hasQuantity = !!String(quantity?.value ?? '').trim();

      if (!sku && !hasQuantity) continue;
      if (!material || !sku) {
        this.clearRowValidationWhenNoMaterial(row);
        continue;
      }

      entries.push({ row, material, quantity: hasQuantity ? quantity : null, sku });
    }

    const skus = [...new Set(entries.map((entry) => entry.sku))];
    /** @type {Map<string, unknown>} Duplicate SKUs across rows resolve to the first entered qty. */
    const quantityBySku = new Map();
    for (const entry of entries) {
      const key = entry.sku.toUpperCase();
      if (entry.quantity && !quantityBySku.has(key)) quantityBySku.set(key, entry.quantity.value);
    }

    const eligibilityBySku = await this.fetchEligibilityResultsBySku(skus, quantityBySku);

    if (eligibilityBySku) {
      const visibilityBySku = await this.fetchSkuVisibilityMap(skus);

      for (const entry of entries) {
        const result = eligibilityBySku.get(entry.sku.toUpperCase());
        const skuFieldValid = this.applyMaterialEligibilityResult(entry.material, result);

        if (skuFieldValid) {
          this.applySkuVisibilityResult(entry.material, entry.sku, visibilityBySku?.[entry.sku]);
        }

        if (entry.quantity) {
          this.applyQuantityEligibilityResult(
            entry.quantity,
            result,
            this.resolveQuantityRulesFromDom(entry.quantity, entry.row)
          );
        }
      }

      await this.applySkuPriceResults(
        entries
          .filter((entry) => !entry.material.closest('.rs-material-number-input-wrapper')?.classList.contains('rs-error'))
          .map((entry) => ({ ...entry, quantity: entry.quantity?.value }))
      );
    }

    return !rows.some(
      (row) =>
        row.querySelector('.rs-material-number-input-wrapper.rs-error') ||
        row.querySelector('.rs-quantity-input-wrapper.rs-error')
    );
  }

  async waitForJwt() {
    if (typeof window.myJWT === 'string' && window.myJWT.trim()) return window.myJWT.trim();
    if (typeof window.resideoWaitForJwt === 'function') {
      await window.resideoWaitForJwt();
    }
    return typeof window.myJWT === 'string' && window.myJWT.trim() ? window.myJWT.trim() : '';
  }

  init() {
    const debouncedPartNumberCheck = this.debounce(this.checkPartNumberInputEligibility.bind(this), 300);
    const debouncedQuantityCheck = this.debounce(this.checkQuantityInputEligibility.bind(this), 300);
    document.addEventListener('input', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) return;
      // Clear the product variant id so that the input can be validated again
      target.dataset.productVariantId = '';
      delete target.dataset.rsValidatedMaterial;
      // File upload populates rows via synthetic input events; defer API checks until blur/submit.
      if (this.isQuickOrderUploadPopulating(target)) return;
      // Quick-order material/SKU still waits for blur; quantity re-validates as the user edits.
      if (this.isQuickOrderInput(target) && target.closest('.rs-material-number-input-wrapper')) return;
      if (target.closest('.rs-material-number-input-wrapper')) {
        debouncedPartNumberCheck(event);
        return;
      }
      if (
        (target.classList.contains('rs-quantity-input') || target.closest('.rs-quantity-input-wrapper')) &&
        !target.closest('.rs-no-validate')
      ) {
        // Emptying a quick-order quantity shows its message at once; edits stay debounced.
        if (this.isQuickOrderInput(target) && !String(target.value ?? '').trim()) {
          this.checkQuantityInputEligibility(event);
          return;
        }
        debouncedQuantityCheck(event);
      }
    });
    document.addEventListener(
      'blur',
      (event) => {
        const target = event.target;
        if (!(target instanceof HTMLInputElement)) return;
        // A SKU already validated on selection keeps its result (and variant id) on blur.
        if (this.isQuickOrderMaterialValidated(target)) return;
        // Clear the product variant id so that the input can be validated again
        target.dataset.productVariantId = '';
        if (!this.isQuickOrderInput(target)) return;
        if (this.isQuickOrderUploadPopulating(target)) return;

        if (target.closest('.rs-material-number-input-wrapper')) {
          this.checkPartNumberInputEligibility(event);
          return;
        }
        if (
          (target.classList.contains('rs-quantity-input') || target.closest('.rs-quantity-input-wrapper')) &&
          !target.closest('.rs-no-validate')
        ) {
          this.checkQuantityInputEligibility(event);
        }
      },
      true
    );
    // Picking an autocomplete suggestion validates right away instead of waiting for blur.
    document.addEventListener('customer-part-number:selected', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement) || !this.isQuickOrderInput(target)) return;
      if (!target.closest('.rs-material-number-input-wrapper')) return;
      if (this.isQuickOrderUploadPopulating(target)) return;
      this.checkPartNumberInputEligibility({ target });
    });
    document.addEventListener('click', (event) => {
      const skuSuggestion = event?.target?.closest('.customer-part-number-suggestion');
      if (skuSuggestion) {
        const wrapper = skuSuggestion.closest('.rs-input__wrapper');
        const input = wrapper?.querySelector('input');
        if (input) {
          debouncedPartNumberCheck({ target: input });
        }
      }
    });
    this.blockScientificNotationOnNumberInputs();
    this.normalizeZeroQuantityInputsOnLoad();
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.normalizeZeroQuantityInputsOnLoad());
    }
  }

  /**
   * Enable quantity inputs and replace zero-only values with 1 on page load.
   * Skips inputs inside `.rs-no-validate` (e.g. warranty rows that stay intentionally disabled).
   */
  normalizeZeroQuantityInputsOnLoad() {
    const isZeroOnlyQuantityValue = (value) => /^0+$/.test(String(value).trim());
    document.querySelectorAll('.rs-quantity-input, .rs-input-type__number').forEach((el) => {
      if (!(el instanceof HTMLInputElement) || el.closest('.rs-no-validate')) return;
      el.removeAttribute('disabled');
      if (isZeroOnlyQuantityValue(el.value)) {
        el.value = '1';
      }
    });
  }

  /**
   * Block invalid characters from being typed or pasted into quantity / number
   * inputs (`input.rs-quantity-input`, `input.rs-input-type__number`).
   * Zero-only values are corrected on `input` (and on load) without blocking keystrokes.
   * `maxlength` is enforced on `type="number"` inputs (which ignore the HTML attribute),
   * including when the full value is selected and replaced with a new digit.
   * `<input type="number">` natively accepts `e`/`E` (scientific notation),
   * `+`/`-`, and `.`; `keydown` is the reliable hook for typing and `paste` covers clipboard input.
   *
   * Resolves the true innermost target via `event.composedPath()[0]` so the
   * handler keeps working if a protected input is ever rendered inside a
   * shadow root (where `event.target` would be retargeted to the host).
   */
  blockScientificNotationOnNumberInputs() {
    const blockedKeys = new Set(['e', 'E', '+', '-', '.']);
    const blockedPaste = /[eE+\-.]/;

    const getProtectedNumberInput = (event) => {
      const path = typeof event.composedPath === 'function' ? event.composedPath() : [event.target];
      const target = path[0];
      if (!(target instanceof HTMLInputElement)) return null;
      return target.classList.contains('rs-input-type__number') || target.classList.contains('rs-quantity-input')
        ? target
        : null;
    };

    const isZeroOnlyQuantityValue = (value) => /^0+$/.test(String(value).trim());

    const resolveQuantityMin = (input) => {
      const min = parseInt(input.min, 10);
      return Number.isFinite(min) && min > 0 ? min : 1;
    };

    const getMaxDigits = (input) => {
      const raw = input.getAttribute('maxlength');
      if (raw == null || raw === '') return null;
      const n = parseInt(raw, 10);
      return Number.isFinite(n) && n > 0 ? n : null;
    };

    /**
     * `type="number"` inputs do not expose selectionStart/selectionEnd in most
     * browsers. When the field is already at max length, treat the next digit as
     * replacing the highlighted value (e.g. after focus selects all).
     */
    const resolveDigitInsertRange = (input, max) => {
      const value = String(input.value ?? '');
      const digitCount = value.replace(/\D/g, '').length;
      const rawStart = input.selectionStart;
      const rawEnd = input.selectionEnd;
      if (rawStart != null && rawEnd != null) {
        return { start: rawStart, end: rawEnd, value };
      }
      if (max != null && digitCount >= max) {
        return { start: 0, end: value.length, value };
      }
      return { start: value.length, end: value.length, value };
    };

    const allowedNavKeys = new Set([
      'Tab',
      'Escape',
      'Enter',
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End',
    ]);

    // The eligibility check sets `step` without a `min`, so stepping an empty field
    // (arrow keys / wheel) would land on -step. Always keep a floor of 1.
    document.addEventListener(
      'focusin',
      (event) => {
        const input = getProtectedNumberInput(event);
        if (input && !(parseInt(input.getAttribute('min'), 10) >= 1)) input.min = '1';
      },
      true
    );

    document.addEventListener('keydown', (event) => {
      const input = getProtectedNumberInput(event);
      if (!input) return;
      // Clear the product variant id so that the input can be validated again
      input.dataset.productVariantId = '';
      if (blockedKeys.has(event.key)) {
        event.preventDefault();
        return;
      }
      if (event.ctrlKey || event.metaKey || event.altKey || allowedNavKeys.has(event.key)) return;
      if (event.key.length !== 1 || !/[0-9]/.test(event.key)) return;

      const max = getMaxDigits(input);
      if (max == null) return;

      const { start, end, value } = resolveDigitInsertRange(input, max);
      const merged = (value.slice(0, start) + event.key + value.slice(end)).replace(/\D/g, '');
      if (merged.length > max) {
        event.preventDefault();
      }
    });

    document.addEventListener('beforeinput', (event) => {
      const input = getProtectedNumberInput(event);
      if (!input || !(event instanceof InputEvent)) return;
      // Clear the product variant id so that the input can be validated again
      input.dataset.productVariantId = '';
      if (event.inputType === 'insertFromPaste') return;
      if (event.inputType?.startsWith('delete')) return;
      const data = event.data;
      if (data == null || data === '' || !/^[0-9]+$/.test(data)) return;

      const max = getMaxDigits(input);
      if (max == null) return;

      const { start, end, value } = resolveDigitInsertRange(input, max);
      const merged = (value.slice(0, start) + data + value.slice(end)).replace(/\D/g, '');
      if (merged.length > max) {
        event.preventDefault();
      }
    });

    document.addEventListener('paste', (event) => {
      const input = getProtectedNumberInput(event);
      if (!input) return;
      // Clear the product variant id so that the input can be validated again
      input.dataset.productVariantId = '';
      const text = event.clipboardData?.getData('text') ?? '';
      if (blockedPaste.test(text)) event.preventDefault();
    });

    document.addEventListener('input', (event) => {
      const input = getProtectedNumberInput(event);
      if (!input || input.disabled) return;

      // Clear the product variant id so that the input can be validated again
      input.dataset.productVariantId = '';

      const max = getMaxDigits(input);
      if (max != null) {
        const cleaned = String(input.value).replace(/\D/g, '').slice(0, max);
        if (input.value !== cleaned) {
          input.value = cleaned;
          try {
            input.setSelectionRange(cleaned.length, cleaned.length);
          } catch (_) {}
        }
      }

      if (!isZeroOnlyQuantityValue(input.value)) return;
      input.value = String(resolveQuantityMin(input));
    });
  }

  /**
   * Debounce helper function
   * @param {Function} fn - Function to debounce
   * @param {number} wait - Wait time in milliseconds
   * @returns {Function} Debounced function
   */
  debounce(fn, wait) {
    /** @type {number | undefined} */
    let timeout;
    return /** @param {...any} args */ function (...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  /**
   * @param {any} result
   */
  resolveQuantityRulesFromApiResult(result) {
    if (!result) return { minQty: 1, shipIncrement: 1 };
    const errors = result.errors || {};
    const minRaw = errors.minOrderQuantity?.value ?? result.minQty;
    const stepRaw = errors.orderMultiple?.value ?? result.shipIncrement;
    const minQty = minRaw === 0 ? 1 : Number(minRaw) || 1;
    const shipIncrement = stepRaw === 0 ? 1 : Number(stepRaw) || 1;
    return { minQty, shipIncrement };
  }

  /**
   * Apply the MOQ / order-multiple rules from an eligibility result to the row's
   * quantity input, and prefill the quantity with the minimum order quantity when
   * the field is empty or below the minimum. A user-entered quantity that already
   * meets the minimum is preserved.
   * @param {Element | null | undefined} inputRow
   * @param {any} result
   */
  applyQuantityRulesAndPrefill(inputRow, result) {
    const quantityInput = inputRow?.querySelector('.rs-quantity-input-wrapper input[type="number"]');
    if (!(quantityInput instanceof HTMLInputElement)) return;

    const { minQty, shipIncrement } = this.resolveQuantityRulesFromApiResult(result);
    quantityInput.min = String(minQty);
    quantityInput.step = String(shipIncrement);
    quantityInput.setAttribute('data-min', String(minQty));

    // Smallest orderable quantity that satisfies both the MOQ and the ship increment.
    let target = minQty;
    if (shipIncrement > 1 && target % shipIncrement !== 0) {
      target = Math.ceil(target / shipIncrement) * shipIncrement;
    }

    const current = Number(String(quantityInput.value ?? '').trim());
    if (!Number.isFinite(current) || current < minQty) {
      quantityInput.value = String(target);
      this.clearQuantityErrorState(quantityInput.closest('.rs-quantity-input-wrapper'));
    }
  }

  /**
   * @param {HTMLInputElement} input
   * @param {Element | null | undefined} inputRow
   */
  resolveQuantityRulesFromDom(input, inputRow) {
    let minQty = NaN;
    let shipIncrement = NaN;

    const attrMin = parseInt(input.getAttribute('min'), 10);
    const dataMin = parseInt(input.getAttribute('data-min'), 10);
    const attrStep = parseInt(input.getAttribute('step'), 10);

    if (Number.isFinite(attrMin) && attrMin >= 1) minQty = attrMin;
    else if (Number.isFinite(dataMin) && dataMin >= 1) minQty = dataMin;

    if (Number.isFinite(attrStep) && attrStep >= 1) shipIncrement = attrStep;

    const blockId =
      inputRow instanceof HTMLElement && inputRow.dataset?.rsBuyButtonsId
        ? String(inputRow.dataset.rsBuyButtonsId).trim()
        : '';
    if (blockId) {
      const activeSa = String(document.body?.dataset?.currentSalesArea ?? '').trim();
      const items = document.querySelectorAll(
        `.rs-availability-table[data-rs-buy-buttons-id="${CSS.escape(blockId)}"] .rs-table__item:not(.rs-hidden)`
      );
      items.forEach((item) => {
        if (Number.isFinite(minQty) && Number.isFinite(shipIncrement)) return;
        const rowSa = String(item.getAttribute('data-sales-area') ?? '').trim();
        if (
          activeSa &&
          rowSa !== activeSa &&
          rowSa.toLowerCase() !== activeSa.toLowerCase()
        ) {
          return;
        }
        const domMin = parseInt(item.getAttribute('data-min-quantity-order'), 10);
        const domStep = parseInt(item.getAttribute('data-ship-increment-quantity'), 10);
        if (!Number.isFinite(minQty) && Number.isFinite(domMin)) minQty = domMin === 0 ? 1 : domMin;
        if (!Number.isFinite(shipIncrement) && Number.isFinite(domStep)) {
          shipIncrement = domStep === 0 ? 1 : domStep;
        }
      });
    }

    return {
      minQty: Number.isFinite(minQty) && minQty >= 1 ? minQty : 1,
      shipIncrement: Number.isFinite(shipIncrement) && shipIncrement >= 1 ? shipIncrement : 1,
    };
  }

  /**
   * @param {number | string} val
   * @param {number | string} minQty
   * @param {number | string} shipIncrement
   */
  isValidQuantityValue(val, minQty, shipIncrement) {
    const valN = Number(val);
    const mo = Math.floor(Number(minQty));
    const st = Math.floor(Number(shipIncrement));
    if (!Number.isFinite(valN) || !Number.isFinite(mo) || valN < mo) return false;
    if (!Number.isFinite(st) || st <= 1) return true;
    return valN === mo || (valN % st === 0 && valN >= mo);
  }

  /**
   * @param {Element | null | undefined} inputWrapper
   */
  clearQuantityErrorState(inputWrapper) {
    if (!inputWrapper) return;
    inputWrapper.classList.remove('rs-error');
    inputWrapper.closest('product-form-component')?.classList.remove('rs-error');
    const errorBox = inputWrapper.querySelector('.rs-error__message');
    const errorText = inputWrapper.querySelector('.rs-error__message-text, .rs-input__error span');
    if (errorText) errorText.textContent = '';
    if (errorBox instanceof HTMLElement) {
      errorBox.style.display = '';
      errorBox.hidden = false;
    }
  }

  /**
   * @param {Element | null | undefined} inputRow
   */
  clearRowValidationWhenNoMaterial(inputRow) {
    if (!inputRow) return;
    const isQuickOrderRow = !!inputRow.closest(QUICK_ORDER_TABLE_SELECTOR);
    const quantityInput = inputRow.querySelector('.rs-quantity-input-wrapper input[type="number"]');
    const hasQuantity = !!String(quantityInput?.value ?? '').trim();
    const materialInput = inputRow.querySelector('.rs-material-number-input-wrapper input');

    this.clearQuantityErrorState(inputRow.querySelector('.rs-quantity-input-wrapper'));
    if (materialInput) delete materialInput.dataset.productVariantId;

    if (isQuickOrderRow && hasQuantity) {
      return;
    }

    inputRow.querySelector('.rs-material-number-input-wrapper')?.classList.remove('rs-error');
  }

  setMaterialError(inputWrapper, inputError, message) {
    if (!inputWrapper) return;
    inputWrapper.classList.add('rs-error');
    if (inputError) inputError.textContent = message;
    const errorBox = inputWrapper.querySelector('.rs-error__message');
    if (errorBox instanceof HTMLElement) {
      errorBox.style.display = '';
      errorBox.hidden = false;
    }
  }

  /**
   * @param {Element | null | undefined} input
   */
  getValidationRow(input) {
    return (
      input?.closest('.rs-table-body__row') ||
      input?.closest('.rs-product-list-detail__info') ||
      input?.closest('.rs-buy-buttons-block')
    );
  }

  /**
   * @param {Element | null | undefined} inputRow
   */
  getRowMaterialSku(inputRow) {
    const fromMaterial = inputRow?.querySelector('.rs-material-number-input-wrapper input');
    if (fromMaterial) {
      const customerPartNumberSku = fromMaterial?.nextElementSibling?.querySelector(`.customer-part-number-suggestion[data-part-number="${fromMaterial.value.trim()}"]`);
      if (customerPartNumberSku && customerPartNumberSku.dataset.partNumber === fromMaterial.value.trim()) {
        return String(customerPartNumberSku.dataset.sku ?? '').trim();
      } else if (fromMaterial.getAttribute('data-selected-sku')) {
        return String(fromMaterial.getAttribute('data-selected-sku') ?? '').trim();
      }
      return String(fromMaterial.value ?? '').trim();
    }

    const card = inputRow?.closest('resideo-product-card') || inputRow?.closest('.rs-product-card');
    const fromDataSku = String(card?.querySelector('[data-sku]')?.dataset?.sku ?? '').trim();
    if (fromDataSku) return fromDataSku;

    return String(inputRow?.querySelector('.rs-product-list-detail__details-sku')?.textContent ?? '').trim();
  }

  async checkQuantityInputEligibility(event) {
    if (!event) return;
    const input = event.target;
    if (this.isQuickOrderUploadPopulating(input)) return;
    const inputWrapper = input.closest('.rs-quantity-input-wrapper');
    const inputRow = this.getValidationRow(input);
    const skuNumberInputWrapper = inputRow?.querySelector('.customer-part-number');

    // Only check eligibility for quantity inputs and ignore inputs with rs-no-validate class
    if (!input.closest('.rs-quantity-input-wrapper') || input.closest('.rs-no-validate')) return;

    // Homepage product cards use rsValidateProductCardQtyInput (search-aligned messages + spacing).
    if (input.closest('.rs-buy-buttons-block')) return;

    const sku = this.getRowMaterialSku(inputRow);
    if (!sku) {
      this.clearRowValidationWhenNoMaterial(inputRow);
      return;
    }

    const quantity = String(input.value ?? '').trim();
    if (!quantity) {
      // Quick order: leave empty alone (required check happens on submit). Elsewhere,
      // reset to min order qty instead of leaving the field empty.
      if (input.closest(QUICK_ORDER_TABLE_SELECTOR)) {
        delete input.dataset.rsValidatedQty;
        const errorSpan = inputWrapper?.querySelector('.rs-error__message-text, .rs-input__error span');
        // A SKU with no quantity is incomplete: as soon as the field is emptied, bring back the
        // quantity rule it was last breaking (else the required message) instead of letting an
        // emptied field silently satisfy the validation.
        if (inputWrapper) {
          if (errorSpan) {
            const knownMin = parseInt(input.getAttribute('data-min'), 10);
            if (input.dataset.rsQtyError) errorSpan.textContent = input.dataset.rsQtyError;
            // Rules learned from an earlier check: an empty field is below the minimum.
            else if (Number.isFinite(knownMin) && knownMin > 1) {
              errorSpan.textContent = /** @type {any} */ (window).__t('minimum_order', { count: knownMin });
            } else errorSpan.innerHTML = errorSpan.dataset.defaultHtml || input.placeholder || '';
          }
          inputWrapper.classList.add('rs-error');
          const errorBox = inputWrapper.querySelector('.rs-error__message');
          if (errorBox instanceof HTMLElement) {
            errorBox.style.display = '';
            errorBox.hidden = false;
          }
          return;
        }
        this.clearQuantityErrorState(inputWrapper);
        return;
      }
      const domRules = this.resolveQuantityRulesFromDom(input, inputRow);
      input.value = String(domRules.minQty);
      this.clearQuantityErrorState(inputWrapper);
      return;
    }

    const domRules = this.resolveQuantityRulesFromDom(input, inputRow);
    // Quick order re-validates a quantity only when its value (or SKU) changed since the last
    // check, so tabbing or clicking through an untouched field does not re-run it.
    const isQuickOrderQty = !!input.closest(QUICK_ORDER_TABLE_SELECTOR);
    const validationKey = `${sku.toUpperCase()}|${quantity}`;
    if (isQuickOrderQty) {
      if (event.type === 'blur' && input.dataset.rsValidatedQty === validationKey) return;
      input.dataset.rsValidatedQty = validationKey;
    }

    // Quick order waits for the API result before touching the message, so an existing
    // error does not blink off and back on while the check is in flight.
    if (
      !input.closest(QUICK_ORDER_TABLE_SELECTOR) &&
      this.isValidQuantityValue(quantity, domRules.minQty, domRules.shipIncrement)
    ) {
      this.clearQuantityErrorState(inputWrapper);
      if (skuNumberInputWrapper?.querySelector('input')?.getAttribute('data-product-variant-id')) skuNumberInputWrapper.classList.remove('rs-error');
    }

    const resultsBySku = await this.fetchEligibilityResultsBySku(
      [sku],
      new Map([[sku.toUpperCase(), quantity]])
    );
    if (!resultsBySku) {
      // The check did not complete, so let the next blur retry it.
      if (isQuickOrderQty) delete input.dataset.rsValidatedQty;
      return;
    }

    if (String(input.value ?? '').trim() !== quantity || this.getRowMaterialSku(inputRow) !== sku) {
      return;
    }

    const result = resultsBySku.get(sku.toUpperCase());
    this.applyQuantityEligibilityResult(input, result, domRules);

    // The "ordered in multiples" error is shown on the SKU field but depends on the quantity,
    // so a quantity change must refresh (set or clear) it. Other SKU errors are left alone.
    if (isQuickOrderQty) {
      const materialInput = inputRow?.querySelector('.rs-material-number-input-wrapper input');
      const materialWrapper = materialInput?.closest('.rs-material-number-input-wrapper');
      if (
        materialInput instanceof HTMLInputElement &&
        (result?.errors?.orderMultiple === false || materialWrapper?.dataset.rsMultiplesError === '1')
      ) {
        this.applyMaterialEligibilityResult(materialInput, result);
      }
    }
  }

  /**
   * Apply MOQ / order-multiple rules from an eligibility result to a quantity input and
   * surface the matching error when the entered quantity breaks them.
   * @param {HTMLInputElement} input
   * @param {any} result Eligibility result for the row SKU (may be undefined)
   * @param {{ minQty: number, shipIncrement: number }} domRules Fallback rules read from the DOM
   */
  applyQuantityEligibilityResult(input, result, domRules) {
    const inputWrapper = input.closest('.rs-quantity-input-wrapper');
    if (!inputWrapper) return;
    const inputError = inputWrapper.querySelector('.rs-error__message-text, .rs-input__error span');
    const quantity = String(input.value ?? '').trim();

    const apiRules = this.resolveQuantityRulesFromApiResult(result);
    const errors = result?.errors || {};
    const minQty =
      errors.minOrderQuantity?.value != null || result?.minQty != null ? apiRules.minQty : domRules.minQty;
    const shipIncrement =
      errors.orderMultiple?.value != null || result?.shipIncrement != null
        ? apiRules.shipIncrement
        : domRules.shipIncrement;
    input.min = String(minQty);
    input.step = String(shipIncrement);
    input.setAttribute('data-min', String(minQty));

    if (this.isValidQuantityValue(quantity, minQty, shipIncrement)) {
      delete input.dataset.rsQtyError;
      this.clearQuantityErrorState(inputWrapper);
      return;
    }

    inputWrapper.classList.add('rs-error');
    const message =
      Number(quantity) < minQty
        ? /** @type {any} */ (window).__t('minimum_order', { count: minQty })
        : /** @type {any} */ (window).__t('order_in_multiples', { count: shipIncrement });
    // Remembered so an emptied field can bring back the rule it was breaking.
    input.dataset.rsQtyError = message;
    if (inputError) inputError.textContent = message;
    if (inputWrapper.querySelector('.rs-error__message') instanceof HTMLElement) {
      inputWrapper.querySelector('.rs-error__message').style.display = '';
    }
  }

  /**
   * Key identifying the SKU a quick-order material field currently resolves to.
   * @param {HTMLInputElement} input
   */
  getMaterialValidationKey(input) {
    return `${input.value.trim()}|${String(input.getAttribute('data-selected-sku') ?? '').trim()}`;
  }

  /**
   * True when a quick-order material field was already validated for its current value.
   * @param {HTMLInputElement} input
   */
  isQuickOrderMaterialValidated(input) {
    return (
      this.isQuickOrderInput(input) &&
      !!input.closest('.rs-material-number-input-wrapper') &&
      !!input.dataset.rsValidatedMaterial &&
      input.dataset.rsValidatedMaterial === this.getMaterialValidationKey(input)
    );
  }

  /**
   * @param {{ target: any; }} event
   */
  async checkPartNumberInputEligibility(event) {
    if (!event) return;
    const input = event.target;
    if (this.isQuickOrderUploadPopulating(input)) return;
    const inputRow = this.getValidationRow(input);

    // Only check eligibility for part number inputs
    if (!input.closest('.rs-material-number-input-wrapper')) return;

    // Get the value of the input
    const customerPartNumberSku = input?.nextElementSibling?.querySelector(`.customer-part-number-suggestion[data-part-number="${input.value.trim()}"]`);
    let value = null;
    if (customerPartNumberSku && customerPartNumberSku.dataset.partNumber === input.value.trim()) {
      value = customerPartNumberSku.dataset.sku;
    } else if (input.getAttribute('data-selected-sku')) {
      value = input.getAttribute('data-selected-sku');
    } else {
      value = input.value;
    }

    const sku = String(value ?? '').trim();

    if (!sku) {
      this.clearRowValidationWhenNoMaterial(inputRow);
      return;
    }

    if (!inputRow?.closest?.(QUICK_ORDER_TABLE_SELECTOR)) {
      await this.validatePartNumberSku(input, inputRow, sku);
      return;
    }

    // quick-order.js keeps add to cart disabled while any SKU field carries this class, so a
    // row cannot be submitted before its eligibility / visibility / price checks settle.
    const materialWrapper = input.closest('.rs-material-number-input-wrapper');
    const validationToken = Symbol('sku-validation');
    input._rsSkuValidationToken = validationToken;
    input.dataset.rsValidatedMaterial = this.getMaterialValidationKey(input);
    materialWrapper?.classList.add(QUICK_ORDER_SKU_VALIDATING_CLASS);
    try {
      await this.validatePartNumberSku(input, inputRow, sku);
    } finally {
      // A newer check on the same field owns the class once it starts.
      if (input._rsSkuValidationToken === validationToken) {
        materialWrapper?.classList.remove(QUICK_ORDER_SKU_VALIDATING_CLASS);
      }
    }
  }

  /**
   * @param {HTMLInputElement} input
   * @param {Element | null} inputRow
   * @param {string} sku
   */
  async validatePartNumberSku(input, inputRow, sku) {
    const rowQuantity = /** @type {HTMLInputElement | null} */ (
      inputRow?.querySelector('.rs-quantity-input-wrapper input[type="number"]')
    );
    const resultsBySku = await this.fetchEligibilityResultsBySku(
      [sku],
      new Map([[sku.toUpperCase(), rowQuantity?.value]])
    );
    if (!resultsBySku) {
      // The check did not complete, so let the next blur retry it.
      delete input.dataset.rsValidatedMaterial;
      return;
    }

    const result = resultsBySku.get(sku.toUpperCase());
    const skuFieldValid = this.applyMaterialEligibilityResult(input, result);
    const errors = result?.errors;
    const skuExists =
      !!result && errors?.skuFound !== false && errors?.accountAuthorized !== false && errors?.productAvailable !== false;

    // For a valid SKU outside quick order, apply quantity rules and prefill MOQ.
    // Quick order skips auto-prefill; quantity rules run when the user edits qty or on submit.
    // A SKU that exists but fails only the multiples rule still needs the SAP price check, so a
    // SKU SAP will not price is flagged. Price it at a valid quantity (the entered one breaks the rule).
    if (!skuFieldValid && skuExists && inputRow?.closest?.(QUICK_ORDER_TABLE_SELECTOR)) {
      const step = Number(result?.shipIncrement) > 0 ? Number(result.shipIncrement) : 1;
      const minQty = Number(result?.minQty) > 0 ? Number(result.minQty) : 1;
      const validQty = Math.ceil(minQty / step) * step;
      await this.applySkuPriceResults([{ row: inputRow, material: input, sku, quantity: validQty }]);
      return;
    }

    if (skuFieldValid) {
      const visibility = await this.checkSkuVisibility(sku);
      // Customer part number picks show the part number in the field and keep the SKU aside,
      // so compare the resolved row SKU rather than the raw input value.
      if (this.getRowMaterialSku(inputRow) !== sku) return;
      if (!this.applySkuVisibilityResult(input, sku, visibility)) return;
      if (inputRow?.closest?.(QUICK_ORDER_TABLE_SELECTOR)) {
        await this.applySkuPriceResults([{ row: inputRow, material: input, sku, quantity: rowQuantity?.value }]);
      } else {
        this.applyQuantityRulesAndPrefill(inputRow, result);
      }
    }
  }

  /**
   * Apply an eligibility result to a material (SKU) input and stamp its variant id.
   *
   * The SKU field reflects ONLY whether the SKU itself is valid for this account: found,
   * authorized and available. Quantity-rule failures (minimum order qty / order multiple)
   * are NOT SKU errors — they are surfaced on the quantity field — so a SKU that fails only
   * the quantity rules stays accepted here.
   * @param {HTMLInputElement} input
   * @param {any} result Eligibility result for this SKU (may be undefined)
   * @returns {boolean} true when the SKU itself is valid for this account
   */
  applyMaterialEligibilityResult(input, result) {
    const inputWrapper = input.closest('.rs-material-number-input-wrapper');
    const inputError = inputWrapper?.querySelector('.rs-error__message-text, .rs-input__error span');

    // Clear the product variant id so that the input can be validated again
    input.dataset.productVariantId = '';

    if (!inputError) {
      inputWrapper?.classList.remove('rs-error');
      return false;
    }

    // The request succeeded but returned nothing for this SKU: it does not exist.
    if (!result) {
      this.setMaterialError(inputWrapper, inputError, /** @type {any} */ (window).__t('sku_not_found'));
      return false;
    }

    let skuFieldValid = true;
    const errors = result.errors;
    delete inputWrapper.dataset.rsMultiplesError;
    if (!errors) {
      inputWrapper.classList.remove('rs-error');
    } else if (
      errors.orderMultiple === false &&
      errors.skuFound !== false &&
      errors.accountAuthorized !== false &&
      errors.productAvailable !== false
    ) {
      // Availability problems below take priority over the multiples rule: a SKU that is not
      // sellable in this channel must not read as a quantity issue.
      if (result?.shipIncrement !== 0 && errors.skuFound !== false) {
        inputError.textContent = /** @type {any} */ (window).__t('must_be_ordered_in_multiples', { count: result.shipIncrement });
        inputWrapper.dataset.rsMultiplesError = '1';
      } else if (errors.skuFound === false) {
        inputError.textContent = /** @type {any} */ (window).__t('sku_not_found');
      } else {
        inputError.textContent = /** @type {any} */ (window).__t('product_unavailable');
      }
      inputWrapper.classList.add('rs-error');
      skuFieldValid = false;
    } else if (errors.skuFound === false) {
      inputError.textContent = /** @type {any} */ (window).__t('sku_not_found');
      inputWrapper.classList.add('rs-error');
      skuFieldValid = false;
    } else if (errors.accountAuthorized === false) {
      inputError.textContent = /** @type {any} */ (window).__t('account_not_authorized');
      inputWrapper.classList.add('rs-error');
      skuFieldValid = false;
    } else if (errors.productAvailable === false) {
      inputError.textContent = /** @type {any} */ (window).__t('product_unavailable');
      inputWrapper.classList.add('rs-error');
      skuFieldValid = false;
    } else {
      inputWrapper.classList.remove('rs-error');
    }

    input.dataset.productVariantId = result.variant_id?.replace('gid://shopify/ProductVariant/', '');

    return skuFieldValid;
  }

  /**
   * Mark a material input unavailable when check-visibility rejects its SKU.
   * @param {HTMLInputElement} input
   * @param {string} sku
   * @param {any} visibility Visibility entry for this SKU (may be null)
   * @returns {boolean} true when the SKU is visible for this context
   */
  applySkuVisibilityResult(input, sku, visibility) {
    if (!visibility || visibility.visible !== false) return true;

    console.warn('Quick order SKU is not visible for this context', {
      sku,
      reason: visibility.reason,
      visibilityReasons: visibility.visibilityReasons,
      availabilityReasons: visibility.availabilityReasons,
      checks: visibility.checks,
    });

    delete input.dataset.productVariantId;
    const inputWrapper = input.closest('.rs-material-number-input-wrapper');
    this.setMaterialError(
      inputWrapper,
      inputWrapper?.querySelector('.rs-error__message-text, .rs-input__error span'),
      /** @type {any} */ (window).__t?.('product_unavailable') || 'Product unavailable'
    );
    return false;
  }

  /**
   * Flag material inputs whose SKU pricing-atp returns without a price. The cart blocks
   * checkout on unpriced lines, so these rows must not be added.
   * @param {Array<{ row: Element, material: HTMLInputElement, sku: string, quantity?: unknown }>} entries
   */
  async applySkuPriceResults(entries = []) {
    if (!entries.length || typeof window.ensureRealtimePricesForAddToCart !== 'function') return;

    let unpricedSkus;
    try {
      ({ unpricedSkus } = await window.ensureRealtimePricesForAddToCart(
        entries.map((entry) => ({
          sku: entry.sku,
          quantity: String(entry.quantity ?? '').trim() || 1,
          variantId: entry.material.dataset.productVariantId || undefined,
        }))
      ));
    } catch (error) {
      console.warn('[RTP] quick order price check failed', error);
      return;
    }

    for (const entry of entries) {
      if (!unpricedSkus?.has(entry.sku)) continue;
      // The shopper may have changed the SKU while pricing was in flight.
      if (this.getRowMaterialSku(entry.row) !== entry.sku) continue;

      // Without a variant id a later valid quantity edit cannot clear this error.
      delete entry.material.dataset.productVariantId;
      const inputWrapper = entry.material.closest('.rs-material-number-input-wrapper');
      this.setMaterialError(
        inputWrapper,
        inputWrapper?.querySelector('.rs-error__message-text, .rs-input__error span'),
        /** @type {any} */ (window).__t('price_unavailable')
      );
      // The price error replaces any multiples message, so a later quantity edit must not clear it.
      delete inputWrapper?.dataset.rsMultiplesError;
    }
  }

  async checkSkuVisibility(sku) {
    const data = await this.fetchSkuVisibilityMap([sku]);
    return data?.[sku] || null;
  }

  /**
   * One `check-visibility` POST for a batch of SKUs.
   * @param {string[]} skus
   * @returns {Promise<Record<string, any> | null>} Response map of sku → visibility entry
   */
  async fetchSkuVisibilityMap(skus = []) {
    if (!skus.length) return null;

    const token = await this.waitForJwt();
    if (!token) return null;

    const payload = this.buildVisibilityPayload(skus);
    if (!payload) return null;

    try {
      const response = await fetch(`${getCustomerAppBaseUrl()}/api/products/check-visibility`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      console.error('Error checking SKU visibility', error);
      return null;
    }
  }

  /**
   * One `check-eligibility` POST for a batch of SKUs.
   * Results are keyed by upper-cased SKU; request order resolves results that omit `sku`.
   * Returns null when the request could not be made, so callers leave the current
   * validation state untouched instead of clearing it.
   * @param {string[]} skus
   * @param {Map<string, unknown>} [quantityBySku] Entered quantities keyed by upper-cased SKU.
   * @returns {Promise<Map<string, any> | null>}
   */
  async fetchEligibilityResultsBySku(skus = [], quantityBySku) {
    if (!skus.length) return null;

    const token = await this.waitForJwt();
    if (!token) return null;

    try {
      const response = await fetch(`${getCustomerAppBaseUrl()}/api/products/check-eligibility`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(this.buildPayload(skus, quantityBySku)),
      });

      if (!response.ok) return null;
      const data = await response.json();
      /** @type {Map<string, any>} */
      const resultsBySku = new Map();
      const results = Array.isArray(data?.results) ? data.results : [];
      results.forEach(/** @param {any} result @param {number} index */ (result, index) => {
        const key = String(result?.sku ?? skus[index] ?? '').trim().toUpperCase();
        if (key) resultsBySku.set(key, result);
      });
      return resultsBySku;
    } catch (error) {
      console.error('Error validating input', error);
      return null;
    }
  }

  /**
   * Quantity for the eligibility payload. An empty or unparseable field means 1, which is also
   * the smallest quantity the API can evaluate its rules against.
   * @param {unknown} value
   * @returns {number}
   */
  normalizeEnteredQuantity(value) {
    const parsed = Number.parseInt(String(value ?? '').trim(), 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  }

  /**
   * @param {any} sku One SKU, or an array of SKUs to check in a single request.
   * @param {Map<string, unknown>} [quantityBySku] Entered quantities keyed by upper-cased SKU.
   */
  buildPayload(sku, quantityBySku) {
    const skus = Array.isArray(sku) ? sku : [sku];
    return {
      company_id: this.companyId || null,
      company_location_gid: this.locationId || null,
      customer_gid: this.customerId || null,
      location_id: this.locationId || null,
      state_code: this.stateCode || null,
      sales_organization: this.salesOrg,
      distribution_channel: this.distributionChannel,
      division: this.division,
      products: skus.map((entry) => ({
        sku: entry,
        quantity: this.normalizeEnteredQuantity(
          quantityBySku?.get(String(entry ?? '').trim().toUpperCase())
        ),
      })),
    };
  }

  buildVisibilityPayload(skus) {
    const context = this.getContextFromCookie();
    if (!context) return null;
    const salesAreaIds = Array.isArray(context.sales_area)
      ? context.sales_area.map((sa) => sa?.fields?.external_id).filter(Boolean)
      : [context.sales_area?.fields?.external_id].filter(Boolean);
    if (!salesAreaIds.length) return null;

    return {
      sku: skus,
      sales_area: salesAreaIds,
      company: context.company_id || null,
      company_external_id: context.company_external_id || null,
      state_code: context.state_code || null,
    };
  }

  getContextFromCookie() {
    if (this.RTP.ctxCache) return this.RTP.ctxCache;
    const ck = this.parseCookies();
    try {
      this.RTP.ctxCache = ck.context ? JSON.parse(decodeURIComponent(ck.context)) : null;
    } catch {
      this.RTP.ctxCache = null;
    }
    return this.RTP.ctxCache;
  }

  parseCookies() {
    const o = {};
    (document.cookie || '').split(';').forEach((s) => {
      const i = s.indexOf('=');
      if (i === -1) return;
      o[decodeURIComponent(s.slice(0, i).trim())] = decodeURIComponent(s.slice(i + 1).trim());
    });
    return o;
  }

  /**
   * @param {{ sales_area?: any; sales_org_id?: any; distribution_channel_id?: any; division_id?: any; }} ctx
   */
  resolveSalesArea(ctx) {
    const cand = ctx?.sales_area;
    const saRaw = Array.isArray(cand) ? cand[0]?.fields || cand[0] : cand?.fields || cand || {};
    return {
      sales_org_id: saRaw?.sales_org_id ?? ctx?.sales_org_id ?? null,
      distribution_channel_id: saRaw?.distribution_channel_id ?? ctx?.distribution_channel_id ?? null,
      division_id: saRaw?.division_id ?? ctx?.division_id ?? null,
      external_id: saRaw?.external_id ?? null,
    };
  }
}

new inputValidation().init();
